import { useEffect } from 'react';
import {
  clearPendingUpdate,
  currentModuleScript,
  hasPendingUpdate,
  indexHtmlUrl,
  isAppUpdateHeld,
  markUpdatePending,
  moduleScriptSrc,
  onAppUpdateHoldChange,
  reloadGuardOpen,
  scriptUrlsMatch,
} from './appUpdate.js';

const POLL_MS = 15000;

function shellUrl() {
  return indexHtmlUrl(import.meta.env.BASE_URL, window.location.origin);
}

async function readShell(url) {
  const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) return null;
  const type = response.headers.get('content-type') || '';
  if (type && !type.includes('html')) return null;
  return response.text();
}

function reloadIfReady() {
  if (!hasPendingUpdate() || isAppUpdateHeld()) return;
  if (!reloadGuardOpen()) return;
  clearPendingUpdate();
  window.location.reload();
}

export function AppUpdate() {
  useEffect(() => {
    if (import.meta.env.DEV) return undefined;

    let stopped = false;
    let checking = false;

    const scheduleReload = () => {
      setTimeout(() => {
        if (!stopped) reloadIfReady();
      }, 0);
    };

    const check = async () => {
      if (stopped || checking) return;
      const current = currentModuleScript();
      if (!current) return;
      checking = true;
      try {
        const url = shellUrl();
        const html = await readShell(url);
        if (stopped || html == null) return;
        const next = moduleScriptSrc(html);
        if (scriptUrlsMatch(current, next, url)) {
          clearPendingUpdate();
          return;
        }
        markUpdatePending();
        scheduleReload();
      } catch {
        // Keep the running build when the shell cannot be read.
      } finally {
        checking = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    const timer = setInterval(check, POLL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('focusout', scheduleReload);
    const unsubscribe = onAppUpdateHoldChange(scheduleReload);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('focusout', scheduleReload);
      unsubscribe();
    };
  }, []);

  return null;
}
